import React, { useEffect } from 'react';
import { LikedUsersProvider } from '@/hooks/likedUsers';
import { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { syncPrivileges } from '../../utils/privilegeSync';
import { cn } from '@/lib/utils';
import { useCanvasClass } from '@/theme/useNightMode';

import Header from './Header';
import SideNav from './SideNav';
import { RailVisibleProvider } from './railVisible';
import ProfileLoader from './ProfileLoader';
import AppLayout from './AppLayout';
import ScrollRestoration from './ScrollRestoration';

const Layout = ({ children }: PropsWithChildren) => {
    const user = useSelector((state: RootState) => state.auth.user);

    const canvasClass = useCanvasClass();
    const userId = user?._id;
    const isAdminInStore = user?.isAdmin === true;
    const isPartnerInStore = user?.isPartner === true;

    useEffect(() => {
        if (!userId) {
            return;
        }

        syncPrivileges(userId, isAdminInStore, isPartnerInStore).catch((err) => {
            console.error('Failed to verify user status', err);
        });
    }, [userId, isAdminInStore, isPartnerInStore]);

    return (
        <LikedUsersProvider>
            <AppLayout>
                <ScrollRestoration />
                <RailVisibleProvider>
                <div className="flex h-screen overflow-hidden bg-neutral-100 text-gray-900 dark:bg-jw-canvas dark:text-jw-ink">
                    <ProfileLoader />
                    <Header />
                    {user && (
                        // The plate stays put. The icon column keeps the logo's box (1.5rem inset,
                        // nudged 8px left, 80px wide). The page columns lock to the notification hub's
                        // left edge and the menu button's right edge, so zoom cannot pull them apart.
                        <aside
                            className="fixed bottom-0 left-0 top-[60px] hidden overflow-hidden bg-neutral-100 dark:border-r dark:border-jw-line dark:bg-jw-chrome lg:block"
                            style={{ width: 'calc(1.5rem + 88.5px)' }}
                        >
                            <div className="absolute inset-y-0 flex w-[80px] min-h-0 flex-col py-4" style={{ left: 'calc(1.5rem - 8px)' }}>
                                <SideNav />
                            </div>
                        </aside>
                    )}
                    <main className={cn('flex-grow overflow-hidden', canvasClass, user && 'lg:ml-[100px]')}>
                        <div id="app-scroll-container" className="h-full overflow-y-auto pt-[90px]" style={{ scrollbarGutter: 'stable both-edges' }}>
                            {children}
                        </div>
                    </main>
                </div>
                </RailVisibleProvider>
            </AppLayout>
        </LikedUsersProvider>
    );
};

export default Layout;
